import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { useState } from 'react';
import { get, qs } from '../api';
import { Empty, ErrorBox, Loading, PageHeader, Progress } from '../components/ui';
import { currentMonth, fmtBytes, fmtDay, fmtMoney, fmtNumber } from '../format';
import type { Pricing, UsageRow } from '../types';

/** Coste en euros si hay tipo de cambio; si no, en dólares. */
function money(cost: { usd: number; eur: number | null }) {
  return cost.eur !== null ? fmtMoney(cost.eur, 'EUR') : fmtMoney(cost.usd, 'USD');
}

function rateNote(p: Pricing) {
  if (p.eurPer1000 === null || p.usdPerEur === null) {
    return `${fmtMoney(p.usdPer1000, 'USD')} por 1.000 páginas · sin tipo de cambio disponible`;
  }
  const source = p.rateSource === 'ecb' && p.rateDate ? `cambio BCE del ${fmtDay(p.rateDate)}` : 'cambio configurado';
  return `${fmtMoney(p.eurPer1000, 'EUR')} por 1.000 páginas · ${source} (1 € = ${p.usdPerEur.toLocaleString('es-ES')} $)`;
}

export function UsagePage() {
  const [month, setMonth] = useState(currentMonth());
  const usage = useQuery({
    queryKey: ['usage', month],
    queryFn: () => get<{ items: UsageRow[]; pricing: Pricing }>(`/usage${qs({ month })}`),
    placeholderData: keepPreviousData,
  });

  const rows = usage.data?.items ?? [];
  const totals = rows.reduce(
    (t, r) => ({
      jobs: t.jobs + r.jobs,
      pages: t.pages + r.pages,
      ocrPages: t.ocrPages + r.ocrPages,
      usd: t.usd + r.cost.usd,
      eur: t.eur === null || r.cost.eur === null ? null : t.eur + r.cost.eur,
    }),
    { jobs: 0, pages: 0, ocrPages: 0, usd: 0, eur: 0 as number | null },
  );
  const pricing = usage.data?.pricing;

  return (
    <>
      <PageHeader
        title="Consumo"
        subtitle="Páginas procesadas por grupo. Solo las páginas con OCR generan coste en Textract."
        actions={<input type="month" className="input" value={month} max={currentMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} />}
      />

      <div className="stats">
        <div className="card stat">
          <div className="label">Trabajos</div>
          <div className="value">{fmtNumber(totals.jobs)}</div>
        </div>
        <div className="card stat">
          <div className="label">Páginas totales</div>
          <div className="value">{fmtNumber(totals.pages)}</div>
        </div>
        <div className="card stat">
          <div className="label">Páginas con OCR</div>
          <div className="value">{fmtNumber(totals.ocrPages)}</div>
        </div>
        <div className="card stat">
          <div className="label">Coste estimado Textract</div>
          <div className="value">{pricing ? money(totals) : '—'}</div>
          {pricing && <div className="cell-sub">{rateNote(pricing)}</div>}
        </div>
      </div>

      <div className="card">
        <ErrorBox error={usage.error} />
        {usage.isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty icon={Activity} title="Sin actividad este mes" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th className="num">Trabajos</th>
                  <th className="num">Con error</th>
                  <th className="num">Páginas</th>
                  <th className="num">Con OCR</th>
                  <th className="num">Coste est.</th>
                  <th>Cuota</th>
                  <th className="num">Volumen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ratio = r.monthlyPageQuota ? r.ocrPages / r.monthlyPageQuota : 0;
                  return (
                    <tr key={r.groupId}>
                      <td className="cell-main">{r.groupName}</td>
                      <td className="num">{fmtNumber(r.jobs)}</td>
                      <td className="num">{r.failed ? <span style={{ color: 'var(--danger)' }}>{fmtNumber(r.failed)}</span> : 0}</td>
                      <td className="num">{fmtNumber(r.pages)}</td>
                      <td className="num">{fmtNumber(r.ocrPages)}</td>
                      <td className="num" title={fmtMoney(r.cost.usd, 'USD')}>
                        {money(r.cost)}
                      </td>
                      <td style={{ minWidth: 180 }}>
                        {r.monthlyPageQuota ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Progress value={ratio} tone={ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : undefined} />
                            <span className="cell-sub">
                              {Math.round(ratio * 100)} % de {fmtNumber(r.monthlyPageQuota)}
                            </span>
                          </div>
                        ) : (
                          <span className="faint">Sin límite</span>
                        )}
                      </td>
                      <td className="num">{fmtBytes(r.bytes)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
